import React, { useState } from 'react'
import Link from 'next/link'

import { useTranslation } from '@mc/i18n'
import {
  Button,
  Dropdown,
  DropdownContentControlled,
  DropdownToggle,
  IconChevronDownDefault,
  IconChevronUpDefault,
} from '@mc/design-system'

import { usePersonaEvents } from '../../events'
import { type BrowseData } from '../../types/browse'
import Image from '../Image'

import classes from './Browse.module.scss'

interface SharedProps {
  onClickCategory: (category: string) => void
  onClickInstructor: (instructor: string) => void
}

const BrowseContent = ({
  categories,
  instructors,
  onClickCategory,
  onClickInstructor,
}: BrowseData & SharedProps) => {
  const { t } = useTranslation('@mc/persona')

  return (
    <div className='d-flex flex-column align-items-start flex-gap-6 mc-px-4 mc-py-6'>
      <div>
        <h3 className='mc-text--bold mc-mb-4'>{t('nav.categories')}</h3>
        <div className='d-flex flex-column'>
          {categories?.map((category) => (
            <Link
              key={category.uuid}
              href={category.caption ? '#' : category.url}
              className={`${classes.link} ${classes.category} mc-px-3 mc-py-2 d-flex align-items-center flex-gap-2`}
              onClick={(evt) => {
                if (category.caption) {
                  evt.preventDefault()
                }
                onClickCategory(category.title)
              }}
            >
              <p>{category.title}</p>
              {category.caption && (
                <span
                  className={`${classes.caption} mc-text-x-small mc-text--capitalize`}
                >
                  {category.caption}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
      <div>
        <h3 className='mc-text--bold mc-mb-4'>{t('nav.instructors')}</h3>
        <div className='d-flex flex-column'>
          {instructors?.map((instructor) => (
            <Link
              key={instructor.uuid}
              href={instructor.ctas?.card ? instructor.ctas?.card : '#'}
              className={`${classes.link} mc-px-3 mc-py-2 mc-w-100`}
              onClick={(evt) => {
                if (!instructor.ctas?.card) {
                  evt.preventDefault()
                }
                onClickInstructor(instructor.text?.title || '')
              }}
            >
              <div className='d-flex flex-gap-2'>
                <Image
                  src={instructor.images?.profile?.url || ''}
                  className={classes.instructorImage}
                  alt=''
                  width={44}
                  height={44}
                />
                <div className='d-flex flex-column'>
                  <div className='d-flex align-items-center flex-gap-2'>
                    <p>{instructor.text?.title || ''}</p>
                    {instructor?.text?.caption && (
                      <span
                        className={`${classes.caption} mc-text-x-small mc-text--capitalize`}
                      >
                        {instructor?.text?.caption}
                      </span>
                    )}
                  </div>
                  <p className='mc-text-x-small'>
                    {instructor.text?.areasOfExpertise?.join(' • ')}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

const BrowseMobile = ({
  isOpen,
  categories,
  instructors,
  onClickCategory,
  onClickInstructor,
}: BrowseData &
  SharedProps & {
    isOpen: boolean
    onClose: () => void
  }) => (
  <div className='d-block d-md-none'>
    {isOpen && (
      <div className={classes.browseMobileContainer}>
        <BrowseContent
          categories={categories}
          instructors={instructors}
          onClickCategory={onClickCategory}
          onClickInstructor={onClickInstructor}
        />
      </div>
    )}
  </div>
)

export const Browse = ({
  categories,
  instructors,
  isMobile = false,
}: { isMobile?: boolean } & BrowseData) => {
  const { t } = useTranslation('@mc/persona')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { trackPersonaSignClick } = usePersonaEvents()

  const handleClickBrowse = () => {
    if (isMobile) {
      const html = document.getElementsByTagName('html')[0]
      if (html.classList.contains('mc-overflow--hidden')) {
        html.classList.remove('mc-overflow--hidden')
      } else {
        html.classList.add('mc-overflow--hidden')
      }
    }
    setIsDropdownOpen((prev) => !prev)
    trackPersonaSignClick({
      cta: 'browse',
      location: 'top navigation',
    })
  }

  const handleClickCategory = (category: string) => {
    trackPersonaSignClick({
      cta: `category: ${category}`,
      location: 'browse drop down',
    })
    setIsDropdownOpen(false)
  }

  const handleClickInstructor = (instructor: string) => {
    trackPersonaSignClick({
      cta: `AI Coaches: ${instructor}`,
      location: 'browse drop down',
    })
  }

  if (isMobile) {
    return (
      <>
        <Button
          kind='link'
          onClick={handleClickBrowse}
          className={` mc-p-3 d-flex align-items-center flex-gap-2
            ${classes.browseButton} ${classes.browseButtonMobile}`}
        >
          {t('nav.browse')}
          {isDropdownOpen ? (
            <IconChevronUpDefault />
          ) : (
            <IconChevronDownDefault />
          )}
        </Button>

        <BrowseMobile
          onClickCategory={handleClickCategory}
          onClickInstructor={handleClickInstructor}
          onClose={() => setIsDropdownOpen(false)}
          categories={categories}
          instructors={instructors}
          isOpen={isDropdownOpen}
        />
      </>
    )
  }

  return (
    <div className='d-flex flex-2'>
      <Dropdown placement='bottom-start'>
        <DropdownToggle>
          <Button
            kind='link'
            onClick={handleClickBrowse}
            className={`mc-p-3 d-flex align-items-center flex-gap-2 ${classes.browseButton}`}
          >
            {t('nav.browse')}
            {isDropdownOpen ? (
              <IconChevronUpDefault />
            ) : (
              <IconChevronDownDefault />
            )}
          </Button>
        </DropdownToggle>
        <DropdownContentControlled
          show={isDropdownOpen}
          className={`${classes.dropdownContainer} mc-mt-2`}
          onClose={() => setIsDropdownOpen(false)}
        >
          <BrowseContent
            categories={categories}
            instructors={instructors}
            onClickCategory={handleClickCategory}
            onClickInstructor={handleClickInstructor}
          />
        </DropdownContentControlled>
      </Dropdown>
    </div>
  )
}
